import { destroyFormCompletely, fetchTrashedForms, restoreForm } from '@/lib/api/trashedFormApi'
import type { RootState } from '@/redux/store'
import { GetTrashedFormsQuery } from '@/typings/domain'
import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit'
import { QueryState } from './queryStateType'

type RecursionNonNullable<T> =
  T extends Record<string, unknown>
    ? {
        [K in keyof T]-?: RecursionNonNullable<NonNullable<T[K]>>
      }
    : T extends any[]
      ? RecursionNonNullable<NonNullable<T[number]>>[]
      : NonNullable<T>

type GetTrashedFormsQueryRequired = RecursionNonNullable<GetTrashedFormsQuery['currentUser']>
type TrashedFormList = GetTrashedFormsQueryRequired['trashedForms']['nodes']
type TrashedFormItem = TrashedFormList[number]

type TrashedFormState = QueryState & {
  pageInfo: {
    totalCount: number
    hasNextPage: boolean
    endCursor: string
  }
}

const initialState: TrashedFormState = {
  isLoading: false,
  loaded: false,
  pageInfo: {
    totalCount: 0,
    hasNextPage: false,
    endCursor: '',
  },
}

const trashedFormAdapter = createEntityAdapter<TrashedFormItem, string>({
  selectId: model => model.token,
})

const trashedFormSlice = createSlice({
  name: 'trashedForm',
  initialState: trashedFormAdapter.getInitialState(initialState),
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchTrashedFormsThunk.pending, state => {
        state.isLoading = true
      })
      .addCase(fetchTrashedFormsThunk.fulfilled, (state, action) => {
        if (action.meta.arg.reset) {
          trashedFormAdapter.setAll(state, action.payload.nodes)
        } else {
          trashedFormAdapter.addMany(state, action.payload.nodes)
        }
        state.pageInfo.totalCount = action.payload.totalCount
        state.pageInfo.hasNextPage = action.payload.pageInfo.hasNextPage
        state.pageInfo.endCursor = action.payload.pageInfo.endCursor
        state.isLoading = false
        state.loaded = true
      })
      .addCase(restoreTrashedFormThunk.fulfilled, (state, action) => {
        trashedFormAdapter.removeOne(state, action.payload)
      })
      .addCase(deleteTrashedFormCompletelyThunk.fulfilled, (state, action) => {
        trashedFormAdapter.removeOne(state, action.payload)
      })
  },
})

type FetchTrashedFormsPayload = {
  reset?: boolean
}

export const fetchTrashedFormsThunk = createAsyncThunk<
  GetTrashedFormsQueryRequired['trashedForms'],
  FetchTrashedFormsPayload,
  { state: RootState }
>('trashedForm/fetchForms', async ({ reset }, thunkAPI) => {
  const endCursor = thunkAPI.getState().trashedForms.pageInfo.endCursor
  const { data } = await fetchTrashedForms({ first: 100, after: reset ? null : endCursor })
  return (
    (data.currentUser?.trashedForms as GetTrashedFormsQueryRequired['trashedForms']) ??
    thunkAPI.rejectWithValue('failed to fetch trashed forms')
  )
})

export const restoreTrashedFormThunk = createAsyncThunk<string, string>('trashedForm/restore', async token => {
  await restoreForm({ id: token })
  return token
})

export const deleteTrashedFormCompletelyThunk = createAsyncThunk<string, string>(
  'trashedForm/deleteCompletely',
  async token => {
    await destroyFormCompletely({ id: token })
    return token
  }
)

export const trashedFormSelectors = trashedFormAdapter.getSelectors<RootState>(state => state.trashedForms)

export default trashedFormSlice.reducer
